import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaChevronDown,
  FaExchangeAlt,
  FaSignOutAlt,
  FaUser,
} from 'react-icons/fa';
import { ROUTES } from '../../assets/constants/strings';
import './style.scoped.scss';

/** Initials for the account-menu avatar, e.g. "Pawan B" -> "PB". */
export const userInitials = (user) => {
  if (!user) return '';
  const first = (user.firstName || '').trim();
  const last = (user.lastName || '').trim();
  const initials = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  return initials || (user.email || '?').charAt(0).toUpperCase();
};

/**
 * The shared "My Account" control (trigger pill + dropdown) used by both the reader
 * and developer-API headers. Render it inside each header's existing
 * `{prefix}__account__item dropdown` wrapper so the surrounding chrome/CSS is unchanged.
 *
 * Props:
 *  - user: the logged-in user (for the identity block).
 *  - items: [{ title, link, icon, onClick? }] — the per-portal menu entries.
 *  - switcher: { label, title, link } | null — the cross-portal switch row (dual accounts only).
 *  - onLogout: () => void — logout handler.
 *  - caption: string | null — a small line under "My Account" (e.g. the SuperAdmin role).
 */
const AccountMenu = ({ user, items = [], switcher, onLogout, caption }) => {
  return (
    <React.Fragment>
      <button
        id='account-dropdown'
        data-toggle='dropdown'
        aria-haspopup='true'
        aria-expanded='false'
        tabIndex={0}
        className='tabIndex-btn'
      >
        <FaUser />
        <span className='account-trigger'>
          <span className='account-trigger__row'>
            <span className='account-trigger__label'>My Account</span>
            <FaChevronDown className='account-trigger__chevron' />
          </span>
          {caption && <span className='account-trigger__caption'>{caption}</span>}
        </span>
      </button>
      <div className='dropdown-menu dropdown-menu-right account-menu'>
        {user && (
          <React.Fragment>
            <div className='account-menu__identity'>
              <div className='account-menu__identity__avatar'>
                {userInitials(user)}
              </div>
              <div className='account-menu__identity__text'>
                <div className='account-menu__identity__name'>
                  {user.displayName}
                </div>
                <div className='account-menu__identity__email'>
                  {user.email}
                </div>
              </div>
            </div>
            <div className='dropdown-divider' />
          </React.Fragment>
        )}

        {items.map((item, i) => (
          <Link key={i} to={item.link} onClick={item.onClick || undefined}>
            <div className='dropdown-item active-override' title={item.title}>
              {item.icon}
              {item.title}
            </div>
          </Link>
        ))}

        {/* Portal switcher — only for accounts holding both product entitlements */}
        {switcher && (
          <React.Fragment>
            <div className='dropdown-divider' />
            <div className='account-menu__label'>Switch product</div>
            <Link to={switcher.link}>
              <div className='dropdown-item active-override' title={switcher.title}>
                <FaExchangeAlt />
                {switcher.label}
              </div>
            </Link>
          </React.Fragment>
        )}

        <div className='dropdown-divider' />
        <Link
          to={ROUTES.LOGIN}
          className='dropdown-item active-override account-menu__logout'
          title='Log out'
          onClick={onLogout}
        >
          <FaSignOutAlt />
          Log out
        </Link>
      </div>
    </React.Fragment>
  );
};

export default AccountMenu;
